<template>
  <aside class="w-96 flex-none bg-white border-l border-gray-200 overflow-y-auto text-xs">
    <p v-if="!ticket && !pullRequest" class="p-4 text-gray-500">
      Select a row to read the verdict and the evidence behind it.
    </p>

    <div v-else-if="ticket" class="p-4">
      <div class="text-gray-500">
        #{{ ticket.requirement }} · {{ ticket.requirement_type }} ·
        {{ ticket.requirement_status }}
      </div>
      <h3 class="font-semibold mt-1">{{ ticket.requirement_title }}</h3>

      <p v-if="!ticket.investigated" class="mt-4 text-gray-500">
        Hades has not investigated this requirement yet, so there is no verdict.
        It will be picked up by the next run.
      </p>

      <p v-else class="mt-4">{{ ticket.story }}</p>

      <!-- An Unknown verdict carries NO recommendation by design: the agent
           could not establish the state, and proposing an action on that basis
           is what this system exists to stop. -->
      <p v-if="isUnknown" class="mt-4 text-gray-500">
        Evidence was insufficient to determine this requirement's state, so
        there is no recommended action.
      </p>
      <p v-else-if="ticket.recommended_action" class="mt-4">
        <span class="font-medium">Recommended:</span> {{ ticket.recommended_action }}
      </p>

      <template v-if="ticket.investigated">
      <h4 class="font-medium mt-4">Verdict</h4>
      <table class="w-full">
        <tr v-for="fact in facts" :key="fact.label" class="border-b border-gray-100">
          <td class="py-1 text-gray-500">{{ fact.label }}</td>
          <td class="py-1 text-right">{{ fact.value }}</td>
        </tr>
      </table>

      <h4 class="font-medium mt-4">Evidence</h4>
      <p v-if="!ticket.citations || !ticket.citations.length" class="text-gray-500">
        No typed evidence was recorded for this verdict.
      </p>
      <div v-for="(cite, i) in ticket.citations" :key="i" class="mt-2">
        <div class="font-mono">{{ cite.namespace }}:{{ cite.id }}</div>
        <div class="text-gray-600">{{ cite.why_it_matters }}</div>
      </div>

      <p v-if="ticket.record_changed_since_investigation" class="mt-4 text-gray-600">
        This requirement changed after Hades investigated it — the verdict may
        describe a record that has since moved.
      </p>

      </template>

      <!-- Only on shipped_record_stale: the one queue with a cited PR behind it. -->
      <div v-if="isCloseable" class="mt-4">
        <button
          type="button"
          class="w-full p-2 border border-gray-300 hover:bg-gray-50"
          @click="pendingClose = true"
        >
          Close requirement #{{ ticket.requirement }}
        </button>
        <p class="mt-1 text-gray-500">Posts the cited evidence as an internal note.</p>
      </div>
    </div>

    <div v-else class="p-4">
      <a
        :href="pullRequest.url"
        target="_blank"
        rel="noopener"
        class="text-primary-700 hover:underline"
      >{{ pullRequest.repository }}#{{ pullRequest.pr_number }}</a>
      <h3 class="font-semibold mt-1">{{ pullRequest.title }}</h3>

      <p class="mt-4">{{ pullRequest.summary }}</p>

      <table class="w-full mt-4">
        <tr v-for="fact in prFacts" :key="fact.label" class="border-b border-gray-100">
          <td class="py-1 text-gray-500">{{ fact.label }}</td>
          <td class="py-1 text-right">{{ fact.value }}</td>
        </tr>
      </table>

      <p v-if="pullRequest.strategic_rationale" class="mt-4">
        {{ pullRequest.strategic_rationale }}
      </p>
    </div>

    <ConfirmationModal
      :is-visible="pendingClose"
      :message="closeMessage"
      @confirm="doClose"
      @cancel="pendingClose = false"
    />
  </aside>
</template>

<script>
import axios from 'axios';
import ConfirmationModal from '@/components/widgetcomponents/ConfirmationModal.vue';
import {
  AXIS_LABELS, CATEGORY_LABELS, CLOSEABLE_QUEUES, DELIVERY_LABELS,
  LINKAGE_LABELS, QUEUE_LABELS, label,
} from './hades_constants';

export default {
  name: 'Hades_Detail',
  components: { ConfirmationModal },
  props: {
    ticket: { type: Object, default: null },
    pullRequest: { type: Object, default: null },
  },
  emits: ['closed'],
  data() {
    return { pendingClose: false };
  },
  computed: {
    isUnknown() {
      return (this.ticket?.queues || []).includes('needs_evidence');
    },
    isCloseable() {
      return (this.ticket?.queues || []).some((q) => CLOSEABLE_QUEUES.includes(q));
    },
    facts() {
      const t = this.ticket;
      if (!t) return [];
      return [
        { label: 'Queue', value: (t.queues || []).map((q) => label(QUEUE_LABELS, q)).join(', ') },
        ...Object.keys(AXIS_LABELS).map((axis) => ({
          label: AXIS_LABELS[axis],
          value: axis === 'delivery' ? label(DELIVERY_LABELS, t.delivery) : t[axis],
        })),
        { label: 'Tenant', value: t.tenant_name || '—' },
        { label: 'Owner', value: t.owner_name || 'Unassigned' },
      ];
    },
    prFacts() {
      const p = this.pullRequest;
      if (!p) return [];
      const reqs = p.requirement_ids || [];
      return [
        { label: 'Category', value: label(CATEGORY_LABELS, p.primary_category) },
        { label: 'Delivery', value: label(DELIVERY_LABELS, p.delivery_state) },
        { label: 'Customer link', value: label(LINKAGE_LABELS, p.customer_linkage) },
        { label: 'Engineer', value: p.engineer_id || p.author_login || '—' },
        { label: 'Requirements', value: reqs.length ? reqs.map((i) => `#${i}`).join(', ') : 'None' },
      ];
    },
    closeMessage() {
      const t = this.ticket;
      if (!t) return '';
      const cites = (t.citations || []).map((c) => `${c.namespace}:${c.id}`).join(', ');
      return [
        `#${t.requirement} — ${t.requirement_title}`,
        '',
        `Hades found this ${label(DELIVERY_LABELS, t.delivery)}.`,
        cites ? `Evidence: ${cites}` : 'No typed evidence recorded.',
        '',
        'The evidence will be posted as an internal note on the requirement.',
      ].join('\n');
    },
  },
  methods: {
    async doClose() {
      this.pendingClose = false;
      try {
        await axios.post('/api/hades/close/', { hades_ticket_id: this.ticket.id });
        this.$emit('closed');
      } catch (e) {
        // 409 is the stale-read guard — the requirement moved after Hades looked
        // at it. Surface it; the verdict needs refreshing, not forcing.
        window.alert(e.response?.data?.detail || 'Could not close the requirement.');
      }
    },
  },
};
</script>
